/**
 * The wire steps of the public-client authorization-code + PKCE flow with PAR (ADR-017 §5), as
 * pure builders: what to POST to `/par`, where to send the user agent, what to POST to `/token`.
 * No browser binding — the `Platform` port (ADR-029 §2) supplies the target and opens the URL,
 * so the same steps serve the web redirect and the native shells' external browser.
 */

/** Which auth server and client id an install signs in against, and where the code comes back. */
export interface AuthTarget {
  /** The OpenID Provider's issuer URL (no trailing slash). */
  readonly issuer: string;
  /** The install's registered public client id. */
  readonly clientId: string;
  /** The exact redirect URI registered for that client. */
  readonly redirectUri: string;
}

/** The in-flight login that must survive the round-trip through the external user agent. */
export interface PendingLogin {
  readonly verifier: string;
  readonly state: string;
  readonly nonce: string;
}

/** A form POST the auth server accepts as a simple request (no preflight). */
export interface FormRequest {
  readonly url: string;
  readonly body: URLSearchParams;
}

/** The pushed authorization request (PAR) for a login. */
export function parRequest(
  target: AuthTarget,
  codeChallenge: string,
  login: PendingLogin,
): FormRequest {
  return {
    url: `${target.issuer}/par`,
    body: new URLSearchParams({
      client_id: target.clientId,
      redirect_uri: target.redirectUri,
      response_type: 'code',
      scope: 'openid email profile',
      code_challenge: codeChallenge,
      code_challenge_method: 'S256',
      state: login.state,
      nonce: login.nonce,
    }),
  };
}

/**
 * The authorize URL the user agent opens: the auth server bounces on to the chosen identity
 * provider (`idp` — one of the ids its /providers catalog advertises; ADR-018).
 */
export function authorizeUrl(target: AuthTarget, requestUri: string, idp: string): string {
  return (
    `${target.issuer}/authorize?client_id=${encodeURIComponent(target.clientId)}` +
    `&request_uri=${encodeURIComponent(requestUri)}` +
    `&idp=${encodeURIComponent(idp)}`
  );
}

/** The code exchange (PKCE) once the code came back. */
export function tokenRequest(target: AuthTarget, code: string, codeVerifier: string): FormRequest {
  return {
    url: `${target.issuer}/token`,
    body: new URLSearchParams({
      grant_type: 'authorization_code',
      code,
      redirect_uri: target.redirectUri,
      client_id: target.clientId,
      code_verifier: codeVerifier,
    }),
  };
}

/** Best-effort email extraction from the ID token, for the header display only. */
export function emailOf(idToken: string | undefined): string | null {
  if (idToken === undefined) return null;
  const parts = idToken.split('.');
  if (parts.length !== 3) return null;
  const segment = parts[1]!.replace(/-/g, '+').replace(/_/g, '/');
  try {
    const payload = JSON.parse(
      atob(segment.padEnd(segment.length + ((4 - (segment.length % 4)) % 4), '=')),
    ) as { email?: unknown };
    return typeof payload.email === 'string' ? payload.email : null;
  } catch {
    return null;
  }
}
